import type { StoreApi } from 'zustand';
import type { SearchStore } from '@/scopes/search/search/infrastructure/store/store';
import { searchStore as defaultSearchStore } from '@/scopes/search/search/infrastructure/store/store';
//TODO move this type
import type { DateOrRange, DepartureDateStore } from '@/infrastructure/departure-date/store';
import { departureDateStore as defaultDepartureDateStore } from '@/infrastructure/departure-date/store';

import { getLocaleDateString } from '@sunwebgroup/ui-components';
import type { SearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/searchQueryRepository';
import { createSearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/searchQueryRepository';
import type { filterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler';

interface Dependencies {
  searchQueryRepository: SearchQueryRepository;
  searchStore: StoreApi<SearchStore>;
  departureDateStore: StoreApi<DepartureDateStore>;
  now: () => Date;
  filterSelectionQueryParamHandler: ReturnType<typeof filterSelectionQueryParamHandler>;
}

export const createSelectDateFilterActions = (dependencies: Partial<Dependencies> = {}) => {
  const {
    searchQueryRepository = createSearchQueryRepository(),
    searchStore = defaultSearchStore,
    departureDateStore = defaultDepartureDateStore,
    now = () => new Date(),
    filterSelectionQueryParamHandler,
  } = dependencies as Dependencies;

  function setDepartureDateFilterSelection(departureDate: DateOrRange) {
    const { setFilterSelection } = searchStore.getState();
    const { setDepartureDate } = departureDateStore.getState();

    setDepartureDate(departureDate);
    setFilterSelection('DepartureDate', departureDate);
    setFilterSelection('Lastminute', undefined);
    filterSelectionQueryParamHandler.update();
  }

  function setLastMinuteFilterSelection(value: string | string[] | undefined) {
    const { setFilterSelection } = searchStore.getState();
    const { setFlexibleRange, setDepartureDate } = departureDateStore.getState();

    const dateRange = extractDateRangeFromLastMinuteSelection(value);

    if (dateRange) {
      setFlexibleRange('exact');
    }
    setDepartureDate(dateRange);
    setFilterSelection('DepartureDate', dateRange);
    setFilterSelection('Lastminute', value);
    filterSelectionQueryParamHandler.update();
  }

  function extractDateRangeFromLastMinuteSelection(value: string | string[] | undefined) {
    const activeQuery = searchQueryRepository.getActiveQuery();
    const lastMinuteOptions = activeQuery?.filters?.Lastminute?.options ?? [];
    const selectedOption = lastMinuteOptions.find((option) => option.value === value);
    const range = selectedOption?.metadata.DaysToDepartureRange.split('|').map((n) => parseInt(n, 10));
    const dateRange = calculateDateRange(range, now());
    return dateRange;
  }

  function calculateDateRange(range: number[] | undefined, originDate: Date): DateOrRange | undefined {
    if (!range) return undefined;

    const startDepartureDate = new Date(originDate);
    startDepartureDate.setUTCDate(originDate.getUTCDate() + range[0]);

    const endDepartureDate = new Date(originDate);
    endDepartureDate.setUTCDate(originDate.getUTCDate() + range[1]);

    return [getLocaleDateString(startDepartureDate), getLocaleDateString(endDepartureDate)];
  }

  return { setDepartureDateFilterSelection, setLastMinuteFilterSelection };
};
